import React, { useEffect, useState } from 'react';
import { Badge, Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { CalculatorApi, ImageCrop, useCalculatorApi } from './calculatorApi';

/** Gemeinsame, schmale Bild-Beschreibung für Vorschau und Lightbox. */
export interface CalcImageData {
    articleNumber: string;
    name: string;
    titel?: string;
    beschreibung?: string;
    haupt?: boolean;
    crop?: ImageCrop | null;
}

// Bearer-geschützte Bilder können nicht direkt als <img src> geladen werden – wir holen den Blob
// authentifiziert und cachen die Object-URLs prozessweit (Bildanzahl ist überschaubar).
const blobUrlCache = new Map<string, Promise<string>>();

const useImageObjectUrl = (api: CalculatorApi, articleNumber: string, name: string): string | undefined => {
    const [url, setUrl] = useState<string | undefined>(undefined);
    useEffect(() => {
        let active = true;
        const key = `${articleNumber}/${name}`;
        let promise = blobUrlCache.get(key);
        if (!promise) {
            promise = api.loadImageBlob(articleNumber, name).then((blob) => URL.createObjectURL(blob));
            blobUrlCache.set(key, promise);
        }
        promise.then((u) => active && setUrl(u)).catch(() => blobUrlCache.delete(key));
        return () => {
            active = false;
        };
    }, [api, articleNumber, name]);
    return url;
};

/**
 * Ein einzelnes Artikelbild in fester Höhe. Mit `crop` wird verzerrungsfrei ein Ausschnitt
 * (Sprite) per `background-position`/`-size` gezeigt; ohne `crop` das ganze Bild als <img>.
 */
export const CalcImage: React.FC<{ image: CalcImageData; height: number }> = ({ image, height }) => {
    const api = useCalculatorApi();
    const url = useImageObjectUrl(api, image.articleNumber, image.name);

    if (!url) {
        return (
            <Box
                sx={{
                    height,
                    width: height,
                    borderRadius: 0.5,
                    bgcolor: 'action.hover',
                }}
            />
        );
    }

    const crop = image.crop;
    if (!crop) {
        return (
            <Box
                component="img"
                src={url}
                alt={image.titel || ''}
                loading="lazy"
                sx={{ height, width: 'auto', maxWidth: '100%', borderRadius: 0.5, objectFit: 'contain' }}
            />
        );
    }

    const width = Math.max(1, Math.round(height * crop.aspectRatio));
    const posX = crop.width < 1 ? (crop.x / (1 - crop.width)) * 100 : 0;
    const posY = crop.height < 1 ? (crop.y / (1 - crop.height)) * 100 : 0;
    return (
        <Box
            title={image.titel || ''}
            sx={{
                display: 'inline-block',
                borderRadius: 0.5,
                width,
                height,
                backgroundImage: `url('${url}')`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: `${100 / crop.width}% ${100 / crop.height}%`,
                backgroundPosition: `${posX}% ${posY}%`,
            }}
        />
    );
};

/** Mini-Vorschau (Haupt- bzw. erstes Bild) mit Anzahl-Badge; Klick öffnet die Lightbox. */
export const ImageThumb: React.FC<{ title: string; images: CalcImageData[] }> = ({ title, images }) => {
    const [open, setOpen] = useState(false);
    if (!images || images.length === 0) return null;
    const main = images.find((i) => i.haupt) || images[0];

    return (
        <>
            <IconButton
                size="small"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen(true);
                }}
                title={(main.titel || main.name) + (images.length > 1 ? ` (+${images.length - 1} weitere)` : '')}
                sx={{ p: 0.25, borderRadius: 0.5 }}
            >
                <Badge
                    color="primary"
                    badgeContent={images.length > 1 ? images.length : 0}
                    overlap="rectangular"
                >
                    <CalcImage image={main} height={26} />
                </Badge>
            </IconButton>
            <ImageLightbox title={title} images={images} open={open} onClose={() => setOpen(false)} />
        </>
    );
};

/** Overlay mit allen Bildern (Titel, Beschreibung, Hauptbild-Markierung). */
export const ImageLightbox: React.FC<{
    title: string;
    images: CalcImageData[];
    open: boolean;
    onClose: () => void;
}> = ({ title, images, open, onClose }) => (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" component="span">
                {title}
            </Typography>
            <IconButton onClick={onClose} size="small">
                <FontAwesomeIcon icon={faXmark} />
            </IconButton>
        </DialogTitle>
        <DialogContent>
            <Box
                sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                }}
            >
                {images.map((img, i) => (
                    <Box key={`${img.name}-${i}`} sx={{ textAlign: 'center' }}>
                        <CalcImage image={img} height={190} />
                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                            {img.haupt && <FontAwesomeIcon icon={faStar} style={{ marginRight: 4 }} />}
                            {img.titel || '(ohne Titel)'}
                        </Typography>
                        {img.beschreibung && (
                            <Typography variant="body2" color="text.secondary">
                                {img.beschreibung}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>
        </DialogContent>
    </Dialog>
);
